/**
 * Copyright IBM Corp. 2022, 2026
 */

import dynamic from 'next/dynamic'

import { slugifyJumpLink } from 'components/jump-nav'

import BasicTable from 'components/basic-table'
import { MDSButton } from '@hashicorp/mds-react/components'
import Hero from 'components/hero'
import Image from 'next/image'
import ReactImage from 'components/optimized-image'
import MiniCallouts from './MiniCallouts'
import SpeakersSection from './SpeakersSection'
import TextAndImage from './TextAndImage'
import formatHeroData from 'lib/formatting/formatHeroData'

const VideoPlayer = dynamic(() => import('react-player'), { ssr: false })

export default function ModularContent({ section, needDistinctIds = false }) {
	// necessary for certain conditions like Resource pages where we need distinct ids for scroll navigation
	// in most pages, we want to preserve the simple human readable id based on headline only
	const jumpLink = needDistinctIds
		? `${section.headline}-${section.id}`
		: section.headline

	switch (section.__typename) {
		case 'BasicTableRecord':
			return (
				<section className="basic-table">
					<BasicTable {...section} />
				</section>
			)

		case 'EmbeddedPodcastSectionRecord':
			return (
				<section className="podcast">
					<div className="soundcloud-container">
						<iframe
							title={section.subscribeTitle}
							width="100%"
							height="166"
							scrolling="no"
							frameBorder="no"
							allow="autoplay"
							src={`https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/${section.soundcloudId}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=true`}
						></iframe>
					</div>
					<div className="podcast-subscribe-links">
						<h5>{section.subscribeTitle}</h5>
						<div className="btn-container">
							{section.soundcloudMainUrl && (
								<MDSButton
									title="Soundcloud"
									external={true}
									url={section.soundcloudMainUrl}
								/>
							)}

							{section.itunesPodcastLink && (
								<MDSButton
									title="iTunes"
									external={true}
									url={section.itunesPodcastLink}
								/>
							)}

							{section.googlePlayMusicUrl && (
								<MDSButton
									title="Google Play Music"
									external={true}
									url={section.googlePlayMusicUrl}
								/>
							)}

							{section.spotifyLink && (
								<MDSButton
									title="Spotify"
									external={true}
									url={section.spotifyLink}
								/>
							)}
						</div>
					</div>
				</section>
			)

		case 'EmbeddedSlidesSectionRecord':
			return (
				<section className="slides">
					<iframe
						title={section.slideshareEmbedLink}
						src={`//www.slideshare.net/slideshow/embed_code/key/${section.slideshareEmbedLink}`}
						width="595"
						height="485"
						frameBorder="0"
						marginWidth="0"
						marginHeight="0"
						scrolling="no"
						style={{
							border: '1px solid #CCC',
							borderWidth: '1px',
							marginBottom: '5px',
							maxWidth: '100%',
						}}
						allowFullScreen
					/>
				</section>
			)

		case 'HeroSectionRecord':
			return (
				<section className="hero-section">
					<Hero {...section} data={formatHeroData(section)} />
				</section>
			)

		case 'ImageSectionRecord':
			return (
				<figure
					className={`modular-content-image-section ${
						section.fullPageWidth ? 'full-width' : ''
					} ${section.alignment || ''}`}
				>
					<ReactImage
						{...section.image}
						{...(section.caption && { alt: section.caption })}
						sizes="calc(100%)"
					/>
					{section.caption ? (
						<figcaption
							className="modular-content-image-caption g-type-caption"
							dangerouslySetInnerHTML={{
								__html: section.caption,
							}}
						/>
					) : null}
				</figure>
			)

		case 'MajorHeadlineSectionRecord':
			return (
				<h2
					className={`major-headline ${
						section.displayType ? 'g-type-' + section.displayType : ''
					}`}
					id={slugifyJumpLink(jumpLink)}
				>
					{section.headline}
				</h2>
			)

		case 'MiniCalloutRecord':
			return (
				<section className="mini-callouts">
					<MiniCallouts callouts={section.items} />
				</section>
			)

		case 'SpeakersSectionRecord':
			return (
				<SpeakersSection title={section.title} speakers={section.speakers} />
			)

		case 'TerraformGraphSectionRecord':
			return (
				<section className="terraform-graph">
					<div className="stack-image">
						{section.stackImages.map((image) => (
							<Image
								key={image.url}
								src={image.url}
								alt={image.alt}
								width={image.width}
								height={image.height}
							/>
						))}
					</div>
					<div className="service-overlay g-grid-container">
						<div className="container">
							<h6>{section.headline}</h6>
							<div className="service-image">
								{section.serviceImages.map((image) => (
									<Image
										key={image.url}
										src={image.url}
										alt={image.alt}
										width={image.width}
										height={image.height}
									/>
								))}
							</div>
						</div>
					</div>
				</section>
			)

		case 'TextHeadlineSectionRecord':
			return (
				<section className="text-headline">
					<h2 className="g-type-display-3">{section.headline}</h2>
					<div
						className="g-type-body"
						dangerouslySetInnerHTML={{ __html: section.text }}
					/>
				</section>
			)

		case 'TextImageSectionRecord':
			return (
				<section className="text-image" id={slugifyJumpLink(section.headline)}>
					<TextAndImage {...section} />
				</section>
			)

		case 'TextSectionRecord':
			return (
				<section
					className="text g-type-body"
					dangerouslySetInnerHTML={{ __html: section.content }}
				/>
			)

		case 'VideoSectionRecord':
			return (
				<section className="video-container">
					<VideoPlayer
						url={section.videoUrl.url}
						width="100%"
						height="100%"
						controls
					/>
				</section>
			)

		default:
			return null
	}
}
